import type { CityConfig } from "../city-template/types";

export const cantonConfig: CityConfig = {
  vitalRecordsIcon: "ri-file-text-line",
  vitalRecordsLinks: [
    { label: "Birth & Death Certificates — Ohio Department of Health", href: "https://odh.ohio.gov/know-our-programs/vital-statistics", description: "Official Ohio vital records office in Columbus" },
    { label: "Marriage Licenses — Stark County Probate Court", href: "https://www.starkcountyohio.gov/probate", description: "Stark County Probate Judge — marriage license issuance" },
    { label: "Divorce Records — Stark County Clerk of Courts", href: "https://www.starkcountyohio.gov/clerk", description: "Stark County Clerk of Courts — divorce decree records" },
    { label: "VitalChek — Order Vital Records Online", href: "https://www.vitalchek.com/", description: "Official partner of Ohio Department of Health for online vital record orders" },
  ],
  city: "Canton",
  state: "Ohio",
  stateAbbr: "OH",
  slug: "canton",
  route: "/cities/canton",
  title: "Emergency Passport Canton, OH — Expedited & Fast Passport Service | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in Canton, OH — as fast as 24 hours. Registered State Dept. courier serving Stark County, Massillon, North Canton, and Alliance. A+ BBB rated. No office visit required.",
  h1: "Emergency & Expedited Passport Services in Canton, OH",
  heroSubtitle: "Need an emergency passport fast in Canton? Fast Passport Center serves all of Stark County with 24-hour courier processing. A+ BBB rated. No drive to Columbus or Chicago required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Canton%20Ohio%20cityscape%20with%20the%20Pro%20Football%20Hall%20of%20Fame%20building%20visible%20along%20the%20skyline%2C%20warm%20afternoon%20light%20casting%20long%20shadows%20across%20downtown%20Canton%2C%20Ohio%20streets%20with%20trees%20and%20brick%20architecture%2C%20American%20flags%20along%20the%20main%20thoroughfare%2C%20clear%20blue%20sky%20above%20the%20Stark%20County%20city%2C%20professional%20urban%20photography%20with%20golden%20hour%20tones&width=1800&height=700&seq=canton-oh-hero-001&orientation=landscape",
  heroImageAlt: "Canton Ohio cityscape with Pro Football Hall of Fame area near downtown Stark County",
  geoPosition: "40.7989;-81.3784",
  geoPlacename: "Canton, Ohio",
  lastModified: "2026-06-14",
  regionalAgency: {
    name: "Chicago Passport Agency",
    address: "230 S Dearborn St, Suite 380, Chicago, IL 60604",
    distance: "Located in downtown Chicago — approximately 350 miles from Canton via I-76 and I-80",
  },
  nearbyCitiesZips: [
    { name: "Canton", zips: "44701–44735" },
    { name: "Massillon", zips: "44646, 44647, 44648" },
    { name: "North Canton", zips: "44720, 44721" },
    { name: "Alliance", zips: "44601" },
    { name: "Wooster", zips: "44691" },
    { name: "Akron", zips: "44301–44320" },
    { name: "New Philadelphia", zips: "44663" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Canton departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-map-pin-line", title: "Stark County Coverage", desc: "Serving Canton, Massillon, North Canton, Alliance and beyond" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Akron, OH", route: "/akron" },
    { name: "Fast passport renewal in Cleveland, OH", route: "/cities/cleveland" },
    { name: "Emergency passport help in Columbus, OH", route: "/cities/columbus-oh" },
    { name: "Urgent passport services in Dayton, OH", route: "/dayton" },
    { name: "Same-day passport help in Cincinnati, OH", route: "/cities/cincinnati" },
    { name: "Passport courier services in Toledo, OH", route: "/toledo" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Canton remotely via secure courier. We do not have a walk-in office in Canton — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Stark County residents.",
  fpcOfficeAddress: "127 Public Square, Cleveland, OH 44114",
  fpcOfficeDistance: "Serving all of Stark County and surrounding areas",
  serviceAreaIntro: "From downtown Canton to the surrounding Stark County communities, Fast Passport Center handles your passport expediting remotely — no need to drive hours to Chicago or Columbus.",
  nearbyCitiesIntro: "We serve Canton, Massillon, North Canton, Alliance, Wooster, and all of Stark County. If you're within driving distance of the Pro Football Hall of Fame, we have you covered.",
  howItWorksIntro: "Most Canton travelers complete everything online and ship documents overnight. No need to drive to Columbus or Chicago.",
  mistakesIntro: "Canton travelers often make these mistakes when applying for an emergency passport. Avoid them to keep your trip on track.",
  testimonialsIntro: "Stark County travelers trust us when their departure is days away. Here's what they have to say.",
  faqIntro: "Common questions about expedited passports from Canton and Stark County.",
  statePageRoute: "/states/ohio",
  statePageName: "Ohio",
  dmvWebsite: "https://www.bmv.ohio.gov",
  dmvLabel: "Ohio BMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For Canton travelers with immediate departure needs — as fast as 24 hours.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for Canton travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens in Stark County." },
    { name: "New Passport", description: "First-time passport application assistance for Canton residents." },
    { name: "Lost or Stolen Passport", description: "Replacement service for lost or stolen passports in Canton OH." },
    { name: "Child Passport", description: "Passport services for minors in the Canton area." },
    { name: "Name Change", description: "Passport correction and name change service for Stark County residents." },
  ],
  testimonials: [
    {
      name: "Brian T.",
      location: "Canton, OH",
      rating: 5,
      text: "My passport expired three weeks before our cruise. Fast Passport Center had my renewal in 5 days — saved the entire vacation. Super easy process.",
      service: "Passport Renewal",
    },
    {
      name: "Melissa F.",
      location: "North Canton, OH",
      rating: 5,
      text: "Business trip to Germany came up with only a week's notice. They got me an expedited passport in 4 days. No drive to Columbus or Chicago needed.",
      service: "Emergency Passport",
    },
    {
      name: "Kevin M.",
      location: "Massillon, OH",
      rating: 5,
      text: "Lost my passport 10 days before our honeymoon. Fast Passport Center guided me through everything and had a replacement in 6 days. Couldn't believe it.",
      service: "Lost or Stolen Passport",
    },
    {
      name: "Sandra L.",
      location: "Alliance, OH",
      rating: 5,
      text: "My son needed his first passport for a school trip abroad. Fast Passport Center handled every step of the child passport process. Incredibly helpful team.",
      service: "Child Passport",
    },
  ],
  facilities: [
    {
      id: "canton-1",
      name: "Canton Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "2650 Cleveland Ave NW",
      city: "Canton",
      state: "OH",
      zip: "44709",
      phone: "(330) 492-3183",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Main Canton postal facility",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Main Canton facility. Call ahead for passport appointment. High volume — arrive early.",
      icon: "ri-mail-line",
    },
    {
      id: "canton-2",
      name: "Massillon Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "210 Lincoln Way E",
      city: "Massillon",
      state: "OH",
      zip: "44646",
      phone: "(330) 833-4821",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "5 miles west of Canton",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Massillon and western Stark County residents.",
      icon: "ri-mail-line",
    },
    {
      id: "canton-3",
      name: "North Canton Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "300 N Main St",
      city: "North Canton",
      state: "OH",
      zip: "44720",
      phone: "(330) 494-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "6 miles north of Canton",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves North Canton and Jackson Township area. Appointment recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "canton-4",
      name: "Stark County Clerk of Courts",
      type: "Court Clerk Passport Acceptance",
      address: "110 Central Plaza S",
      city: "Canton",
      state: "OH",
      zip: "44702",
      phone: "(330) 451-7823",
      hours: "Mon–Fri: 8:30 AM – 4:30 PM",
      distance: "Downtown Canton courthouse",
      website: "https://www.starkcountyohio.gov/clerk",
      websiteLabel: "Stark County Courts",
      notes: "Accepts passport applications at the courthouse. Government photo ID required.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — limited slots at Chicago Agency", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 350 mile drive to Chicago", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    { icon: "ri-image-line", title: "Wrong Photo Size or Background", description: "Passport photos must be exactly 2×2 inches with a white or off-white background. Many Canton photo shops use incorrect sizes." },
    { icon: "ri-file-list-line", title: "Incomplete DS-11 Form", description: "Missing signatures or wrong form type are the top rejection reasons at Stark County acceptance facilities." },
    { icon: "ri-money-dollar-circle-line", title: "Wrong Payment Amount", description: "Fees change frequently. Show up with incorrect payment and you'll need to reschedule." },
    { icon: "ri-calendar-close-line", title: "No Proof of Travel", description: "Without flight confirmation or travel itinerary, emergency agency appointments will be denied." },
    { icon: "ri-parent-line", title: "Missing Parental Consent", description: "For minors, both parents must appear or submit notarized consent. Many Stark County families miss this." },
    { icon: "ri-time-line", title: "Waiting Too Long to Start", description: "Standard processing takes 8–11 weeks. Start expedited at least 3 weeks before your Canton departure." },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send a prepaid overnight label. Drop documents at any UPS location in Canton or Stark County." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Canton address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Canton, OH before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Canton for travelers with urgent international flights. Our registered couriers hand-deliver your documents to U.S. Department of State facilities, achieving 24–48 hour turnaround without the long drive to Chicago." },
    { q: "How fast can I renew my passport in Canton?", a: "Fast Passport Center offers passport renewal in Canton in as little as 24–48 hours through our emergency courier service. For less urgent travel, our 3–5 day expedited option is the best value. Both include full document review and a dedicated agent." },
    { q: "Is there a passport agency near Canton, OH?", a: "The nearest regional passport agency to Canton is the Chicago Passport Agency, approximately 350 miles away. For urgent processing without the long drive, Fast Passport Center's registered courier service achieves 24–48 hour turnaround from Canton." },
    { q: "Do I need to visit an acceptance facility in Canton?", a: "First-time applicants must visit an acceptance facility for form DS-11 in person. Renewals can often be handled entirely by mail. Fast Passport Center will tell you exactly what's needed for your specific situation." },
    { q: "What passport services are available in Stark County?", a: "Fast Passport Center serves all of Stark County including Canton, Massillon, North Canton, Alliance, Louisville, and surrounding communities. All services are handled remotely via overnight courier — no in-person visit to our office required." },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Canton", address: "4432 Dressler Rd NW, Canton, OH 44718", hours: "Mon–Sun: 8AM–10PM", website: "https://www.cvs.com/" },
    { name: "Walgreens — Canton", address: "3801 Whipple Ave NW, Canton, OH 44718", hours: "Mon–Sun: 7AM–11PM", website: "https://www.walgreens.com/" },
    { name: "Walmart Supercenter — Canton", address: "4736 Dressler Rd NW, Canton, OH 44718", hours: "Mon–Sun: 6AM–11PM", website: "https://www.walmart.com/" },
    { name: "FedEx Office — North Canton", address: "6380 Strip Ave NW, North Canton, OH 44720", hours: "Mon–Fri: 8AM–8PM, Sat: 9AM–6PM", website: "https://www.fedex.com/" },
    { name: "CVS Pharmacy — Massillon", address: "3300 Lincoln Way E, Massillon, OH 44646", hours: "Mon–Sun: 8AM–10PM", website: "https://www.cvs.com/" },
  ],
  dmvLocations: [
    {
      name: "Stark County BMV — Canton",
      address: "4573 Dressler Rd NW, Canton, OH 44718",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Driver's license, REAL ID, and title services for Stark County residents.",
      featured: true,
    },
    {
      name: "AAA — Canton",
      address: "4700 Hills & Dales Rd NW, Canton, OH 44708",
      hours: "Mon–Fri: 8:30AM–5:30PM, Sat: 9AM–12PM",
      note: "AAA members can access license renewal and passport photo services.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "CAK",
      name: "Akron-Canton Regional Airport",
      distance: "12 miles north of Canton",
      address: "5400 Lauby Rd NW, North Canton, OH 44720",
      note: "Primary airport serving Stark County. Airlines include Allegiant, American, Delta, and United.",
      flightUrl: "https://akroncantonairport.com/",
      flightLabel: "Akron-Canton Airport",
      featured: true,
    },
    {
      code: "CLE",
      name: "Cleveland Hopkins International Airport",
      distance: "56 miles north of Canton",
      address: "5300 Riverside Dr, Cleveland, OH 44135",
      note: "Major hub with broader international routes. Best option for long-haul international trips.",
      flightUrl: "https://www.clevelandairport.com/",
      flightLabel: "Cleveland Hopkins Airport",
      featured: false,
    },
  ],
};